import { useState } from 'react';
import type { AssetObject } from '../shared/assets.ts';
import { cleanId } from '../shared/clean-id.ts';
import { api, unwrap } from './api.ts';

type Attachment = {
  id: string;
  name: string;
  file?: File;
  asset?: { id: string; fileName: string; byteSize: number };
  status: 'uploading' | 'ready' | 'failed';
  error?: unknown;
};
export function useChatAttachments(resolveChatId: () => Promise<string>) {
  const [items, setItems] = useState<Attachment[]>([]);
  const [error, setError] = useState<unknown>();
  const update = (id: string, change: Partial<Attachment>) =>
    setItems((items) => items.map((item) => (item.id === id ? { ...item, ...change } : item)));
  const upload = async (item: Attachment) => {
    if (!item.file) return;
    update(item.id, { status: 'uploading', error: undefined });
    try {
      const chatId = await resolveChatId();
      const asset = unwrap(
        await api.PUT('/api/chats/{chatId}/attachments/{attachmentId}/bytes', {
          params: {
            path: { chatId, attachmentId: item.id },
            query: { fileName: item.file.name },
          },
          headers: { 'Content-Type': 'application/octet-stream' },
          body: item.file,
          bodySerializer: (file) => file,
        }),
      );
      update(item.id, { status: 'ready', asset });
    } catch (error) {
      update(item.id, { status: 'failed', error });
    }
  };
  const tooLarge = items.reduce((sum, item) => sum + (item.asset?.byteSize ?? 0), 0) > 40 * 1024 * 1024;
  return {
    items,
    ids: items.flatMap((item) => (item.status === 'ready' ? [item.id] : [])),
    blocked: items.some((item) => item.status !== 'ready') || tooLarge,
    error: tooLarge ? new Error('Keep the combined attachments in one message below 40 MiB.') : error,
    add: (files: File[]) => {
      if (files.length + items.length > 20) {
        setError(new Error('Attach up to 20 files in one message.'));
        return;
      }
      setError(undefined);
      const added: Attachment[] = files.map((file) => ({
        id: cleanId(),
        name: file.name,
        file,
        status: 'uploading',
      }));
      setItems((items) => [...items, ...added]);
      for (const item of added) void upload(item);
    },
    addExisting: (asset: AssetObject) => {
      if (items.some((item) => item.id === asset.id)) return;
      if (items.length >= 20) {
        setError(new Error('Attach up to 20 files in one message.'));
        return;
      }
      setItems((items) => [...items, { id: asset.id, name: asset.fileName, asset, status: 'ready' }]);
    },
    remove: (id: string) => setItems((items) => items.filter((item) => item.id !== id)),
    clearSent: (ids: string[]) => setItems((items) => items.filter((item) => !ids.includes(item.id))),
    retry: (id: string) => {
      const item = items.find((item) => item.id === id);
      if (item) void upload(item);
    },
  };
}
export type AttachmentQueue = ReturnType<typeof useChatAttachments>;
